/**
 * Eigenes Konto: Name, Passwort, zweiter Faktor, angemeldete Geräte.
 *
 * Der zweite Faktor wird in zwei Schritten eingerichtet: Erst der QR-Code,
 * dann ein gültiger Code aus der App. Erst dann ist TOTP aktiv — sonst
 * sperrt man sich mit einem falsch abgescannten Code selbst aus.
 *
 * Die Backup-Codes erscheinen genau einmal. Danach existieren sie nur noch
 * als Hash in der Datenbank; wer sie nicht notiert hat, hat sie verloren.
 */
import { useState, type ReactNode } from 'react';
import { Textfeld } from '../bausteine/Feld';
import { Seite } from '../bausteine/Layout';
import { Fehlermeldung, Meldung } from '../bausteine/Meldung';
import { Laedt } from '../bausteine/Zustand';
import { useAbruf, useVorgang } from '../lib/abruf';
import { api } from '../lib/api';
import { datumZeit } from '../lib/format';
import { useSitzung } from '../sitzung/SitzungKontext';
import type { SitzungZeile, TotpEinrichtung } from '../lib/typen';

export function Konto(): ReactNode {
  const { benutzer, neuLaden: sitzungNeuLaden } = useSitzung();
  const sitzungen = useAbruf<SitzungZeile[]>('/api/sitzungen');

  return (
    <Seite titel="Konto" untertitel={benutzer?.email}>
      <div className="stapel" style={{ maxWidth: '820px' }}>
        <Angaben neuLaden={sitzungNeuLaden} />
        <Passwort />
        <ZweiterFaktor />
        <Geraete sitzungen={sitzungen} />
      </div>
    </Seite>
  );
}

// =========================================================================
//  Name und E-Mail
// =========================================================================

function Angaben({ neuLaden }: { neuLaden: () => Promise<void> }): ReactNode {
  const { benutzer } = useSitzung();
  const vorgang = useVorgang();
  const [name, setzeName] = useState(benutzer?.name ?? '');
  const [email, setzeEmail] = useState(benutzer?.email ?? '');
  const [gespeichert, setzeGespeichert] = useState(false);

  const geaendert = name !== benutzer?.name || email !== benutzer?.email;

  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const ergebnis = await vorgang.fuehreAus(() =>
      api.aendern('/api/konto', {
        ...(name !== benutzer?.name ? { name: name.trim() } : {}),
        ...(email !== benutzer?.email ? { email: email.trim() } : {}),
      }),
    );
    if (ergebnis !== undefined) {
      setzeGespeichert(true);
      await neuLaden();
    }
  }

  return (
    <form className="karte" onSubmit={(e) => void speichern(e)}>
      <div className="karte-kopf">
        <h2>Angaben</h2>
      </div>
      <div className="karte-koerper stapel">
        <Fehlermeldung text={vorgang.fehler} />
        {gespeichert ? <Meldung ton="gut">Gespeichert.</Meldung> : null}
        <div className="formularraster">
          <Textfeld
            beschriftung="Name"
            wert={name}
            setzeWert={(w) => {
              setzeName(w);
              setzeGespeichert(false);
            }}
          />
          <Textfeld
            beschriftung="E-Mail-Adresse"
            art="email"
            wert={email}
            setzeWert={(w) => {
              setzeEmail(w);
              setzeGespeichert(false);
            }}
            hinweis="Damit meldest du dich an"
          />
        </div>
        <div className="reihe">
          <button type="submit" className="knopf" disabled={!geaendert || vorgang.laeuft}>
            Speichern
          </button>
        </div>
      </div>
    </form>
  );
}

// =========================================================================
//  Passwort
// =========================================================================

function Passwort(): ReactNode {
  const vorgang = useVorgang();
  const [alt, setzeAlt] = useState('');
  const [neu, setzeNeu] = useState('');
  const [wiederholung, setzeWiederholung] = useState('');
  const [fertig, setzeFertig] = useState(false);

  const zuKurz = neu !== '' && neu.length < 12;
  const passtNicht = wiederholung !== '' && neu !== wiederholung;
  const moeglich = alt !== '' && neu.length >= 12 && neu === wiederholung;

  async function aendern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden('/api/konto/passwort', { altesPasswort: alt, neuesPasswort: neu }),
    );
    if (ergebnis !== undefined) {
      setzeAlt('');
      setzeNeu('');
      setzeWiederholung('');
      setzeFertig(true);
    }
  }

  return (
    <form className="karte" onSubmit={(e) => void aendern(e)}>
      <div className="karte-kopf">
        <h2>Passwort</h2>
      </div>
      <div className="karte-koerper stapel">
        <Fehlermeldung text={vorgang.fehler} />
        {fertig ? (
          <Meldung ton="gut">
            Passwort geändert. Andere Geräte bleiben angemeldet — bei Verdacht
            unten einzeln abmelden.
          </Meldung>
        ) : null}

        <div className="formularraster">
          <Textfeld
            beschriftung="Bisheriges Passwort"
            art="password"
            wert={alt}
            setzeWert={setzeAlt}
            pflicht
            breit
            hinweis="Wird verlangt, damit an einem unbeaufsichtigten Rechner niemand den Zugang übernimmt"
          />
          <Textfeld
            beschriftung="Neues Passwort"
            art="password"
            wert={neu}
            setzeWert={setzeNeu}
            pflicht
            fehler={zuKurz ? 'Mindestens 12 Zeichen.' : null}
          />
          <Textfeld
            beschriftung="Wiederholung"
            art="password"
            wert={wiederholung}
            setzeWert={setzeWiederholung}
            pflicht
            fehler={passtNicht ? 'Stimmt nicht überein.' : null}
          />
        </div>

        <div className="reihe">
          <button type="submit" className="knopf" disabled={!moeglich || vorgang.laeuft}>
            Passwort ändern
          </button>
        </div>
      </div>
    </form>
  );
}

// =========================================================================
//  Zweiter Faktor
// =========================================================================

function ZweiterFaktor(): ReactNode {
  const vorgang = useVorgang();
  const [einrichtung, setzeEinrichtung] = useState<TotpEinrichtung | null>(null);
  const [code, setzeCode] = useState('');
  const [backupCodes, setzeBackupCodes] = useState<string[] | null>(null);

  async function starten(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden<TotpEinrichtung>('/api/totp/einrichten'),
    );
    if (ergebnis !== undefined) setzeEinrichtung(ergebnis);
  }

  async function aktivieren(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden<{ backupCodes: string[] }>('/api/totp/aktivieren', { code: code.trim() }),
    );
    if (ergebnis !== undefined) {
      setzeBackupCodes(ergebnis.backupCodes);
      setzeEinrichtung(null);
      setzeCode('');
    }
  }

  return (
    <div className="karte">
      <div className="karte-kopf">
        <h2>Zweiter Faktor</h2>
        <span className="klein still">TOTP</span>
      </div>
      <div className="karte-koerper stapel">
        <Fehlermeldung text={vorgang.fehler} />

        {backupCodes ? (
          <>
            <Meldung ton="gut" titel="Zweiter Faktor aktiv.">
              Ab jetzt verlangt die Anmeldung zusätzlich einen Code aus der App.
            </Meldung>
            <Meldung ton="warnung" titel="Diese Backup-Codes jetzt ausdrucken.">
              Sie erscheinen nie wieder — gespeichert ist nur ihr Hash. Jeder
              gilt einmal. Zu den Steuerunterlagen legen, nicht in den
              Passwort-Manager auf demselben Gerät.
            </Meldung>
            <div className="karte">
              <div className="karte-koerper fest" style={{ lineHeight: 2 }}>
                {backupCodes.map((c) => (
                  <div key={c}>{c}</div>
                ))}
              </div>
            </div>
          </>
        ) : einrichtung ? (
          <>
            <Meldung ton="info">
              QR-Code in der Authenticator-App scannen, dann den angezeigten
              Code eintragen. Erst danach ist der zweite Faktor aktiv.
            </Meldung>
            <div className="reihe" style={{ alignItems: 'flex-start' }}>
              <img
                src={einrichtung.qrCode}
                alt="QR-Code für die Authenticator-App"
                style={{ border: '1px solid var(--rand)', borderRadius: 'var(--radius-klein)' }}
              />
              <div className="stapel">
                <div>
                  <div className="klein leise">Falls der Code nicht scannbar ist:</div>
                  <div className="fest">{einrichtung.geheimnis}</div>
                </div>
                <Textfeld
                  beschriftung="Code aus der App"
                  wert={code}
                  setzeWert={setzeCode}
                  platzhalter="123456"
                  pflicht
                />
                <div className="reihe">
                  <button
                    type="button"
                    className="knopf haupt"
                    disabled={code.trim().length < 6 || vorgang.laeuft}
                    onClick={() => void aktivieren()}
                  >
                    Aktivieren
                  </button>
                  <button type="button" className="knopf" onClick={() => setzeEinrichtung(null)}>
                    Abbrechen
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="klein still" style={{ margin: 0 }}>
              Hier liegen Steuerdaten. Ein Passwort allein ist dafür zu wenig —
              der zweite Faktor kostet einmal zwei Minuten Einrichtung.
            </p>
            <div className="reihe">
              <button
                type="button"
                className="knopf"
                disabled={vorgang.laeuft}
                onClick={() => void starten()}
              >
                Zweiten Faktor einrichten
              </button>
            </div>
            <p className="klein still" style={{ margin: 0 }}>
              Ist er bereits aktiv, meldet die API einen Konflikt — zum Wechseln
              muss er auf der Konsole zurückgesetzt werden.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// =========================================================================
//  Angemeldete Geräte
// =========================================================================

function Geraete({
  sitzungen,
}: {
  sitzungen: ReturnType<typeof useAbruf<SitzungZeile[]>>;
}): ReactNode {
  const vorgang = useVorgang();

  async function abmelden(id: string): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() => api.entfernen(`/api/sitzungen/${id}`));
    if (ergebnis !== undefined) sitzungen.neuLaden();
  }

  return (
    <div className="karte">
      <div className="karte-kopf">
        <h2>Angemeldete Geräte</h2>
      </div>
      {!sitzungen.daten ? (
        <Laedt />
      ) : (
        <div className="tabelle-rahmen">
          <table className="tabelle">
            <thead>
              <tr>
                <th>Gerät</th>
                <th className="schmal">Art</th>
                <th className="schmal">Letzte Nutzung</th>
                <th className="schmal">Läuft ab</th>
                <th className="schmal" />
              </tr>
            </thead>
            <tbody>
              {sitzungen.daten.map((s) => (
                <tr key={s.id}>
                  <td>
                    {s.geraet ?? 'ohne Namen'}
                    {s.aktuell ? <span className="marker gut" style={{ marginLeft: '0.5rem' }}>dieses Gerät</span> : null}
                  </td>
                  <td className="schmal">{s.typ === 'app' ? 'App' : 'Browser'}</td>
                  <td className="schmal klein still">{datumZeit(s.letzteNutzungAm)}</td>
                  <td className="schmal klein still">{datumZeit(s.laeuftAbAm)}</td>
                  <td className="schmal">
                    {s.aktuell ? null : (
                      <button
                        type="button"
                        className="knopf klein gefahr"
                        disabled={vorgang.laeuft}
                        onClick={() => void abmelden(s.id)}
                      >
                        Abmelden
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="karte-fuss klein still">
        Ein Widerruf wirkt sofort — die Sitzung wird bei der nächsten Anfrage
        abgewiesen. Browser-Sitzungen laufen nach acht Stunden Untätigkeit ab,
        App-Sitzungen nach dreißig Tagen.
      </div>
    </div>
  );
}
